// Shift Control — opened from the Attendance page action bar. Shifts (hours +
// the single check-in location) and the per-day roster that assigns each
// employee a shift or an extra rest day. Self-contained like
// DefaultLocationModal: the button owns the open state, and the content (and
// its data fetching) only mounts while the modal is open.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, CalendarRange, Clock, Shield, X } from "lucide-react";
import AlertModal from "../../../../components/AlertModal";
import { getTopBarButtonStyle } from "../../styles/attendanceStyles";
import { useShiftControl } from "./useShiftControl";
import { makeStyles, scrollbarCss } from "./shiftControlStyles";
import { text } from "./shiftControlText";
import ScheduleTab from "./ScheduleTab";
import ShiftsTab from "./ShiftsTab";

function ShiftControlContent({ onClose }) {
  const sc = useShiftControl();
  const { theme, language, t, profileLoading, can, alertModal, setAlertModal } = sc;
  const isRTL = language === "ar";
  const styles = useMemo(() => makeStyles(theme, isRTL), [theme, isRTL]);
  const [tab, setTab] = useState("schedule");
  const activeTab = can.assign ? tab : "shifts";
  const [scheduleDirty, setScheduleDirty] = useState(false);
  // Footer slot: the Schedule tab portals its unsaved-changes bar here.
  const [footerEl, setFooterEl] = useState(null);

  // "Discard unsaved changes?" — system AlertModal instead of window.confirm.
  const [discardPrompt, setDiscardPrompt] = useState(null); // { onYes, onNo }
  const confirmDiscard = useCallback((onYes, onNo) => setDiscardPrompt({ onYes, onNo }), []);
  const answerDiscard = (yes) => {
    const prompt = discardPrompt;
    setDiscardPrompt(null);
    if (yes) prompt?.onYes?.();
    else prompt?.onNo?.();
  };

  const requestClose = useCallback(() => {
    if (scheduleDirty) confirmDiscard(onClose);
    else onClose();
  }, [scheduleDirty, confirmDiscard, onClose]);

  const switchTab = (next) => {
    if (next === activeTab) return;
    const go = () => { setScheduleDirty(false); setTab(next); };
    if (scheduleDirty) confirmDiscard(go);
    else go();
  };

  // Esc closes, unless a nested dialog (shift form / alert) or an open
  // dropdown / calendar menu is on top — then Esc only closes that. Capture
  // phase so the check runs before the menu removes itself.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(
        "[data-modal-root='true'], [data-shift-form='true'], .reusable-dropdown-portal, [data-datepicker-portal='true']",
      )) return;
      requestClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [requestClose]);

  // Keep the page behind from scrolling while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div style={{ ...styles.overlay, zIndex: 9000 }} onClick={requestClose}>
      <style>{scrollbarCss(styles.dark)}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
        onClick={(e) => e.stopPropagation()}
        style={styles.dialog}
      >
        <AlertModal
          open={alertModal.open}
          title={alertModal.title}
          message={alertModal.message}
          variant={alertModal.variant}
          onConfirm={() => setAlertModal({ ...alertModal, open: false })}
          onClose={() => setAlertModal({ ...alertModal, open: false })}
          theme={theme}
          language={language}
        />
        <AlertModal
          open={Boolean(discardPrompt)}
          title={t.discardTitle}
          message={t.discardConfirm}
          variant="warning"
          showCancel
          onConfirm={() => answerDiscard(true)}
          onClose={() => answerDiscard(false)}
          theme={theme}
          language={language}
        />

        <div style={styles.header}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0, flex: "1 1 280px" }}>
            <div style={styles.headerIcon}><CalendarClock size={22} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 style={styles.title}>{t.title}</h2>
              <p style={styles.subtitle}>{t.subtitle}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            {can.assign && can.any && (
              <div style={styles.tabs} role="tablist">
                <button role="tab" aria-selected={activeTab === "schedule"} style={styles.tab(activeTab === "schedule")}
                  onClick={() => switchTab("schedule")}>
                  <CalendarRange size={16} /> {t.tabSchedule}
                </button>
                <button role="tab" aria-selected={activeTab === "shifts"} style={styles.tab(activeTab === "shifts")}
                  onClick={() => switchTab("shifts")}>
                  <Clock size={16} /> {t.tabShifts}
                </button>
              </div>
            )}
            <button className="sc-close" style={styles.closeBtn} onClick={requestClose} aria-label={t.cancel}><X size={22} /></button>
          </div>
        </div>

        <div className="sc-noscroll" style={styles.body}>
          {profileLoading ? (
            <div style={styles.emptyState}>{t.loading}</div>
          ) : !can.any ? (
            <div style={{ ...styles.card, ...styles.emptyState }}>
              <Shield size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
              <div style={{ fontWeight: 600 }}>{t.unauthorized}</div>
            </div>
          ) : activeTab === "schedule" ? (
            <ScheduleTab
              styles={styles} t={t} theme={theme} language={language} meta={sc.meta}
              fetchRoster={sc.fetchRoster} saveRoster={sc.saveRoster} copyWeek={sc.copyWeek}
              notifyError={sc.notifyError} notifySuccess={sc.notifySuccess}
              onDirtyChange={setScheduleDirty}
              confirmDiscard={confirmDiscard}
              footerEl={footerEl}
            />
          ) : (
            <ShiftsTab
              styles={styles} t={t} theme={theme} language={language} can={can} meta={sc.meta}
              shifts={sc.shifts} loading={sc.shiftsLoading}
              saveShift={sc.saveShift} setShiftActive={sc.setShiftActive} deleteShift={sc.deleteShift}
              notifyError={sc.notifyError}
            />
          )}
        </div>
        <div ref={setFooterEl} className="sc-footer" style={styles.footer} />
      </div>
    </div>
  );
}

/** Action-bar button that opens Shift Control. */
export default function ShiftControlModal({ theme, language, isRTL }) {
  const [open, setOpen] = useState(false);
  const t = text[language] || text.en;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ ...getTopBarButtonStyle(theme, false), flexDirection: isRTL ? "row-reverse" : "row" }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 1)" : "rgba(51, 65, 85, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)";
        }}
      >
        <CalendarClock size={16} />
        {t.title}
      </button>
      {/* Portaled to <body>: the Attendance header uses backdrop-filter, which
          would otherwise become the containing block of this fixed overlay and
          trap the modal inside the action bar. */}
      {open && createPortal(<ShiftControlContent onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}
